import { Button, Show } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { CenterQuestionnaireContent } from "components/InAppQuestionnaire/InAppQuestionnaire";
import Translate from "components/Translate/Translate";
import type { FC } from "react";
import { HashAction, HashBottomSheet } from "../HashActions";
import SkipLabsConfirmation from "../SkipLabsConfirmation/SkipLabsConfirmation";
import InitialLabsBloc from "./InitialLabsBloc";
import image from "src/ui/assets/lab-report-photo.webp";

const ContainerFirstStep = styled.div`
  max-width: 442px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  img {
    border-radius: 1em;
    margin-bottom: 16px;
    aspect-ratio: 16/10;
    background-color: var(--color-charcoal-10);
    width: 100%;
    object-fit: cover;
  }

  p:last-of-type {
    margin-top: 0;
    margin-bottom: 24px;
  }
`;

interface FirstStepProps {
  onContinue: () => void;
}

const FirstStep: FC<FirstStepProps> = ({ onContinue }) => {
  const [
    state,
    { setShowLearnMore, setShowSkipInitialLabsConfirmation, skipInitialLabs }
  ] = useBlocNext(InitialLabsBloc);

  return (
    <>
      <CenterQuestionnaireContent>
        <ContainerFirstStep>
          <img src={image} aria-hidden />
          <nine-spacer s="md" />
          <h4>
            <Translate msg="task.yourInitialLab.questionnaire.title" />
          </h4>
          <nine-spacer s="md" />
          <p>
            <Translate msg="task.yourInitialLab.questionnaire.description" />
          </p>
          <Button onPress={onContinue} theme="sunrise" grow>
            <Translate msg="task.yourInitialLab.questionnaire.cta" />
          </Button>

          <Show when={state.canSkipInitialLabsButton}>
            <nine-spacer s="md" />
            <Button
              onPress={() => setShowSkipInitialLabsConfirmation(true)}
              theme="danger"
              outline
              grow
              hideArrow
              style={
                {
                  "--inner-background": "var(--color-cream)",
                  "--border-size": "1px"
                } as React.CSSProperties
              }
            >
              <Translate
                msg="skipInitialLabs.short"
                variables={{ context: "labsRequired" }}
              />
            </Button>
          </Show>

          <HashBottomSheet
            hash={HashAction.whyLabsMatter}
            trackingName="Why Labs Matter Sheet"
          >
            <h4>
              <Translate msg="whyLabsMatter.title" />
            </h4>
            <p>
              <Translate msg="whyLabsMatter.intro" />
            </p>
            <ul>
              <li>
                <Translate msg="whyLabsMatter.hba1c" />
              </li>
              <li>
                <Translate msg="whyLabsMatter.tsh" />
              </li>
              <li>
                <Translate msg="whyLabsMatter.creatinine" />
              </li>
            </ul>
            <p>
              <Translate msg="whyLabsMatter.healthHistory" />
            </p>
            <p>
              <Translate msg="whyLabsMatter.eligibility" />
            </p>
          </HashBottomSheet>
        </ContainerFirstStep>
      </CenterQuestionnaireContent>

      <Show when={state.showSkipInitialLabsConfirmation}>
        <SkipLabsConfirmation
          onClose={() => setShowSkipInitialLabsConfirmation(false)}
          onConfirm={() => void skipInitialLabs()}
          trackingName="Initial Labs Dialog Skip Confirmation"
        />
      </Show>
    </>
  );
};

export default FirstStep;
